/* eslint-disable @typescript-eslint/typedef */
import { EUserMediaEvent, UserMedia } from '@streaming/mouflon-utils';
import React from 'react';

const isSrcObjectAvailable = (videoElement: HTMLVideoElement): boolean =>
  videoElement?.srcObject !== undefined;

export const useUserMedia = ({
  videoElement,
}: {
  videoElement?: HTMLVideoElement | null;
} = {}) => {
  const userMediaRef = React.useRef<UserMedia | null>(null);
  const [availableDevices, setAvailableDevices] = React.useState<{
    audio: MediaDeviceInfo[];
    video: MediaDeviceInfo[];
  }>({ audio: [], video: [] });
  const [constraints, setConstraints] = React.useState<MediaStreamConstraints>({});
  const [mediaStream, setMediaStream] = React.useState<MediaStream | null>(null);
  const [isPermissionsRequested, setPermissionsRequested] = React.useState<boolean>(false);
  const [error, setError] = React.useState<Error | null>(null);

  const updateDevices = async () => {
    if (userMediaRef.current) {
      const audio: MediaDeviceInfo[] = await userMediaRef.current.getAudioInputDevices();
      const video: MediaDeviceInfo[] = await userMediaRef.current.getVideoDevices();
      setAvailableDevices({
        audio,
        video,
      });
    }
  };

  const requestPermissions = React.useCallback(() => {
    return userMediaRef.current?.requestPermissions().then(() => {
      setPermissionsRequested(true);
      updateDevices();
    });
  }, [userMediaRef.current]);

  const attachListeners = React.useCallback(() => {
    userMediaRef.current?.addEventListener(EUserMediaEvent.MEDIA_DEVICES_UPDATED, updateDevices);
  }, [userMediaRef.current]);

  const detachListeners = React.useCallback(() => {
    userMediaRef.current?.removeEventListener(EUserMediaEvent.MEDIA_DEVICES_UPDATED, updateDevices);
  }, [userMediaRef.current]);

  const destroy = React.useCallback(() => {
    userMediaRef.current?.destroy();
  }, []);

  React.useEffect(() => {
    if (constraints && Object.keys(constraints).length > 0 && userMediaRef.current) {
      if (
        !Object.values(constraints).some((c) =>
          typeof c === 'object' ? !!c.deviceId || c.facingMode : !!c,
        )
      ) {
        return;
      }

      userMediaRef.current
        ?.getMediaStream(constraints, { sizeThreshold: 50, baseAspectRatio: 1.333 })
        .then((mediaStream) => {
          setMediaStream(mediaStream);

          updateDevices();
        })
        .catch((e) => {
          setError(e);
        });
    }
  }, [userMediaRef.current, constraints]);

  React.useEffect(() => {
    if (videoElement) {
      if (mediaStream) {
        if (isSrcObjectAvailable(videoElement)) {
          videoElement.srcObject = mediaStream;
        } else {
          videoElement.src = globalThis.URL?.createObjectURL(mediaStream as unknown as MediaSource);
        }

        videoElement.play();
      } else {
        if (isSrcObjectAvailable(videoElement)) {
          videoElement.srcObject = null;
        } else {
          globalThis.URL?.revokeObjectURL(videoElement.src);
        }
      }
    }
  }, [videoElement, mediaStream]);

  React.useEffect(() => {
    userMediaRef.current = new UserMedia();
    attachListeners();

    return () => {
      destroy();
      detachListeners();
    };
  }, []);

  return {
    requestPermissions,
    setConstraints,
    mediaStream,
    isPermissionsRequested,
    availableVideoDevices: availableDevices.video,
    availableAudioDevices: availableDevices.audio,
    destroy,
    error,
  };
};
